import React, { useState, useEffect } from 'react';
import api, { playersAPI, evaluationsAPI, trialPeriodsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Evaluations.css';

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  team?: {
    name: string;
  };
}

interface EvaluationCategory {
  id: string;
  name: string;
  description: string;
  metrics: string[];
}

interface TrialPeriod {
  id: string;
  name: string;
  season: string;
  year: number;
}

interface EvaluationResult {
  id: string;
  player: {
    id: string;
    firstName: string;
    lastName: string;
    team?: {
      name: string;
    };
  };
  category: {
    id: string;
    name: string;
  };
  season: string;
  evaluationDate: string;
  data: Record<string, any>;
  trialPeriod?: TrialPeriod;
  allEvaluations?: Array<{
    id: string;
    evaluationDate: string;
    data: Record<string, any>;
    season: string;
    trialPeriod?: TrialPeriod;
  }>;
  evaluationCount?: number;
}

const Evaluations: React.FC = () => {
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState<EvaluationCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [results, setResults] = useState<EvaluationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [selectedTrialPeriod, setSelectedTrialPeriod] = useState<string>('');
  const [trialPeriods, setTrialPeriods] = useState<TrialPeriod[]>([]);
  
  // Add/Edit functionality
  const [showAddModal, setShowAddModal] = useState(false);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [newEvaluationData, setNewEvaluationData] = useState<Record<string, string>>({});
  const [editingCell, setEditingCell] = useState<{ resultId: string; metric: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  
  // Expanded rows for showing historical evaluations
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingHistoryCell, setEditingHistoryCell] = useState<{ evalId: string; metric: string } | null>(null);
  const [historyEditValue, setHistoryEditValue] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchPlayers();
    fetchTrialPeriods();
  }, []);

  const fetchTrialPeriods = async () => {
    try {
      const response = await trialPeriodsAPI.getAll();
      setTrialPeriods(response.data);
    } catch (err: any) {
      console.error('Failed to load trial periods:', err);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      // Reset sort when category changes
      setSortField('');
      fetchCategoryResults(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/evaluations/categories');
      setCategories(response.data);
      if (response.data.length > 0) {
        setSelectedCategory(response.data[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await playersAPI.getAll();
      // Filter out coaches
      const players = response.data.filter((p: any) => 
        p.position !== 'Coach' && p.position !== 'Manager' && p.position !== 'Owner'
      );
      setAllPlayers(players);
    } catch (err: any) {
      console.error('Failed to load players:', err);
    }
  };

  const fetchCategoryResults = async (categoryId: string) => {
    setLoading(true);
    try {
      const response = await api.get(`/evaluations/category/${categoryId}/grouped`);
      setResults(response.data);
      
      // Extract unique team names
      const teams = new Set<string>();
      response.data.forEach((result: EvaluationResult) => {
        if (result.player.team?.name) {
          teams.add(result.player.team.name);
        }
      });
      setAvailableTeams(Array.from(teams).sort());
      
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamToggle = (teamName: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamName)
        ? prev.filter(t => t !== teamName)
        : [...prev, teamName]
    );
  };

  const handleAddEvaluation = (playerId?: string) => {
    setShowAddModal(true);
    setSelectedPlayer(playerId || '');
    // Initialize newEvaluationData with empty strings for all metrics
    const initialData: Record<string, string> = {};
    selectedCategoryData?.metrics.forEach(metric => {
      initialData[metric] = '';
    });
    setNewEvaluationData(initialData);
  };

  const handleSaveNewEvaluation = async () => {
    if (!selectedPlayer || !selectedCategory) {
      alert('Please select a player');
      return;
    }

    try {
      // Convert string values to numbers
      const data: Record<string, number> = {};
      Object.entries(newEvaluationData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          data[key] = parseFloat(value);
        }
      });

      await evaluationsAPI.create({
        playerId: selectedPlayer,
        categoryId: selectedCategory,
        data,
        season: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
      });

      setShowAddModal(false);
      fetchCategoryResults(selectedCategory);
      alert('Evaluation added successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save evaluation');
    }
  };

  const handleCellClick = (resultId: string, metric: string, currentValue: any) => {
    // Only allow editing for admins
    if (!isAdmin) return;
    
    setEditingCell({ resultId, metric });
    setEditValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const result = results.find(r => r.id === editingCell.resultId);
    if (!result) return;

    try {
      const newData = { ...result.data };
      newData[editingCell.metric] = editValue ? parseFloat(editValue) : null;

      await evaluationsAPI.update(editingCell.resultId, { data: newData });
      
      // Update local state
      setResults(prev => prev.map(r => 
        r.id === editingCell.resultId 
          ? { ...r, data: newData }
          : r
      ));
      
      setEditingCell(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update evaluation');
      setEditingCell(null);
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleDeleteEvaluation = async (resultId: string, playerName: string) => {
    if (!window.confirm(`Are you sure you want to delete the evaluation for ${playerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await evaluationsAPI.delete(resultId);
      
      // Remove from local state
      setResults(prev => prev.filter(r => r.id !== resultId));
      
      alert('Evaluation deleted successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete evaluation');
    }
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  // Get actual metrics from results data (aggregated - no attempts)
  const actualMetrics = React.useMemo(() => {
    if (results.length === 0) return [];
    
    // Collect all unique metric keys from all results
    const metricsSet = new Set<string>();
    results.forEach(result => {
      if (result.data) {
        Object.keys(result.data).forEach(key => {
          // Filter out empty strings and whitespace-only strings
          if (key && key.trim()) {
            metricsSet.add(key);
          }
        });
      }
    });
    
    // Sort metrics with custom order: Right columns before Left columns
    return Array.from(metricsSet).sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      // Check if metrics contain "right" or "left"
      const aHasRight = aLower.includes('right');
      const aHasLeft = aLower.includes('left');
      const bHasRight = bLower.includes('right');
      const bHasLeft = bLower.includes('left');
      
      // If comparing Right with Left (regardless of other words), Right comes first
      if (aHasRight && !aHasLeft && bHasLeft && !bHasRight) return -1;
      if (aHasLeft && !aHasRight && bHasRight && !bHasLeft) return 1;
      
      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });
  }, [results, selectedCategoryData]);

  // Set default sort to first metric when category data loads (rank 1 to last)
  useEffect(() => {
    if (actualMetrics.length > 0) {
      setSortField(actualMetrics[0]);
      setSortDirection('asc'); // ascending rank (1, 2, 3, etc.)
    }
  }, [actualMetrics]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredResults = React.useMemo(() => {
    let filtered = results.map(result => {
      // Always recalculate best scores from allEvaluations (whether specific period or all periods)
      if (result.allEvaluations && result.allEvaluations.length > 0) {
        // Filter evaluations to selected period (or use all if no period selected)
        const periodEvaluations = selectedTrialPeriod
          ? result.allEvaluations.filter(evaluation => evaluation.trialPeriod?.id === selectedTrialPeriod)
          : result.allEvaluations; // Use ALL evaluations when no period selected
        
        // If no evaluations in this period, exclude this player
        if (periodEvaluations.length === 0) {
          return null;
        }
        
        // Recalculate best scores for the selected evaluations
        const metrics = new Set<string>();
        periodEvaluations.forEach(evaluation => {
          Object.keys(evaluation.data || {}).forEach(metric => metrics.add(metric));
        });
        
        const bestData: Record<string, number> = {};
        for (const metric of Array.from(metrics)) {
          const values = periodEvaluations
            .map(evaluation => evaluation.data[metric])
            .filter(v => v !== undefined && v !== null && !isNaN(parseFloat(String(v))));
          
          if (values.length > 0) {
            const categoryName = selectedCategoryData?.name.toLowerCase() || '';
            const metricName = metric.toLowerCase();
            const isTimedDrill = !metricName.includes('figure') && (
              categoryName.includes('sprint') || categoryName.includes('agility') ||
              categoryName.includes('dribble') || metricName.includes('sprint') ||
              metricName.includes('dribble') || metricName.includes('agility')
            );
            const isHigherBetter = !isTimedDrill;
            
            bestData[metric] = isHigherBetter
              ? Math.max(...values.map(v => parseFloat(String(v))))
              : Math.min(...values.map(v => parseFloat(String(v))));
          }
        }
        
        return {
          ...result,
          data: bestData,
          allEvaluations: periodEvaluations,
          evaluationCount: periodEvaluations.length
        };
      }
      
      return result;
    }).filter(result => result !== null) as EvaluationResult[];
    
    // Filter by selected teams
    if (selectedTeams.length > 0) {
      filtered = filtered.filter(result => 
        result.player.team?.name && selectedTeams.includes(result.player.team.name)
      );
    }
    
    return filtered;
  }, [results, selectedTeams, selectedTrialPeriod, selectedCategoryData]);

  // Calculate ranks for each metric based on filtered results  
  const calculateRanks = React.useMemo(() => {
    const ranks: Record<string, Record<string, number>> = {}; // metric -> resultId -> rank
    
    actualMetrics.forEach(metric => {
      // Get all results with values for this metric
      const resultsWithValues = filteredResults
        .map(result => ({
          id: result.id,
          value: result.data[metric],
          isDNF: result.data[metric] === 'DNF' || result.data[metric] === 'dnf',
          didNotParticipate: result.data[metric] === undefined || 
                              result.data[metric] === null || 
                              result.data[metric] === '' ||
                              result.data[metric] === 0
        }));

      // Separate DNF, non-participants, and valid scores
      const validResults = resultsWithValues.filter(r => !r.isDNF && !r.didNotParticipate);
      const dnfResults = resultsWithValues.filter(r => r.isDNF);
      const nonParticipants = resultsWithValues.filter(r => r.didNotParticipate);

      // Determine if higher or lower is better
      const categoryName = selectedCategoryData?.name.toLowerCase() || '';
      const metricName = metric.toLowerCase();
      
      // Figure 8 is higher is better (count-based, not time-based)
      const isFigure8 = categoryName.includes('figure 8') || categoryName.includes('figure8') || 
                        metricName.includes('figure 8') || metricName.includes('figure8');
      
      // Timed drills: lower is better (fastest time wins) - excluding Figure 8
      const isTimedDrill = !isFigure8 && (
        categoryName.includes('sprint') ||
        categoryName.includes('agility') ||
        categoryName.includes('speed dribble') ||
        categoryName.includes('zig') ||
        categoryName.includes('dribble') ||
        metricName.includes('sprint') ||
        metricName.includes('agility') ||
        metricName.includes('dribble') ||
        metricName.includes('zig') ||
        metricName.includes('illinois')
      );
      
      // For timed drills, lower is better. For everything else (including Figure 8), higher is better
      const isHigherBetter = !isTimedDrill;

      // Sort valid results
      validResults.sort((a, b) => {
        const aNum = parseFloat(String(a.value));
        const bNum = parseFloat(String(b.value));
        return isHigherBetter ? bNum - aNum : aNum - bNum; // Reverse for higher-is-better
      });

      // Assign ranks with tie handling
      ranks[metric] = {};
      let currentRank = 1;
      
      for (let i = 0; i < validResults.length; i++) {
        const currentValue = parseFloat(String(validResults[i].value));
        
        // Check if this value is the same as the previous one (tie)
        if (i > 0) {
          const previousValue = parseFloat(String(validResults[i - 1].value));
          if (Math.abs(currentValue - previousValue) < 0.0001) { // Account for floating point precision (0.1 millisecond tolerance)
            // Same value as previous, use same rank
            ranks[metric][validResults[i].id] = ranks[metric][validResults[i - 1].id];
          } else {
            // Different value, use current position as rank
            currentRank = i + 1;
            ranks[metric][validResults[i].id] = currentRank;
          }
        } else {
          // First result
          ranks[metric][validResults[i].id] = currentRank;
        }
      }

      // DNF gets last place (tied)
      const lastPlace = validResults.length + 1;
      dnfResults.forEach(r => {
        ranks[metric][r.id] = lastPlace;
      });

      // Non-participants get the place after DNF (or after valid results if no DNF)
      const nonParticipantPlace = validResults.length + (dnfResults.length > 0 ? 2 : 1);
      nonParticipants.forEach(r => {
        ranks[metric][r.id] = nonParticipantPlace;
      });
    });

    return ranks;
  }, [filteredResults, actualMetrics]);

  // Apply sorting after ranks are calculated
  const filteredAndSortedResults = React.useMemo(() => {
    if (!sortField) return filteredResults;

    return [...filteredResults].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'name') {
        aValue = `${a.player.firstName} ${a.player.lastName}`;
        bValue = `${b.player.firstName} ${b.player.lastName}`;
      } else if (sortField === 'team') {
        aValue = a.player.team?.name || '';
        bValue = b.player.team?.name || '';
      } else {
        // Sorting by metric - use rank instead of value
        aValue = calculateRanks[sortField]?.[a.id] || 999;
        bValue = calculateRanks[sortField]?.[b.id] || 999;
      }

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

        // For metrics, we're sorting by rank (lower rank = better)
      if (actualMetrics.includes(sortField)) {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // For name/team sorting
      const comparison = String(aValue).localeCompare(String(bValue));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredResults, sortField, sortDirection, calculateRanks, actualMetrics]);

  const getSortIndicator = (field: string) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleRowExpansion = (playerId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const handleDeleteHistoricalEvaluation = async (evalId: string, playerName: string, date: string) => {
    if (!window.confirm(`Are you sure you want to delete the evaluation for ${playerName} from ${new Date(date).toLocaleDateString()}?`)) {
      return;
    }

    try {
      await evaluationsAPI.delete(evalId);
      // Refresh the data
      fetchCategoryResults(selectedCategory);
      alert('Evaluation deleted successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete evaluation');
    }
  };

  const formatValue = (value: any, metric: string): string => {
    if (value === undefined || value === null) return '-';
    if (typeof value !== 'number') return String(value);

    const categoryName = selectedCategoryData?.name.toLowerCase() || '';
    const metricName = metric.toLowerCase();
    
    const isTimedDrill = 
      categoryName.includes('sprint') ||
      categoryName.includes('agility') ||
      categoryName.includes('dribble') ||
      categoryName.includes('figure') ||
      metricName.includes('time') ||
      metricName.includes('sprint') ||
      metricName.includes('dribble') ||
      metricName.includes('agility') ||
      metricName.includes('figure');
    
    const isBeepTest = categoryName.includes('beep');
    const isBroadJump = categoryName.includes('broad jump') || categoryName.includes('broad');
    
    const decimals = isTimedDrill ? 3 : isBeepTest ? 1 : isBroadJump ? 2 : 0;
    return value.toFixed(decimals);
  };

  const handleHistoryCellClick = (evalId: string, metric: string, currentValue: any) => {
    if (!isAdmin) return;
    
    setEditingHistoryCell({ evalId, metric });
    setHistoryEditValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
  };

  const handleHistoryCellBlur = async () => {
    if (!editingHistoryCell) return;

    try {
      // Find the current evaluation data from the results
      let currentEvalData: Record<string, any> = {};
      
      // Search through all results to find the evaluation
      for (const result of results) {
        if (result.allEvaluations) {
          const foundEval = result.allEvaluations.find(e => e.id === editingHistoryCell.evalId);
          if (foundEval) {
            currentEvalData = foundEval.data;
            break;
          }
        }
      }
      
      // Update just the one metric
      const updatedData = { ...currentEvalData };
      updatedData[editingHistoryCell.metric] = historyEditValue ? parseFloat(historyEditValue) : null;

      await evaluationsAPI.update(editingHistoryCell.evalId, { data: updatedData });
      
      // Refresh the data to show updated values and recalculate ranks
      fetchCategoryResults(selectedCategory);
      
      setEditingHistoryCell(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update evaluation');
      setEditingHistoryCell(null);
    }
  };

  const handleHistoryCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleHistoryCellBlur();
    } else if (e.key === 'Escape') {
      setEditingHistoryCell(null);
    }
  };

  return (
    <div className="evaluations-page">
      <div className="evaluations-header">
        <h1>📊 Player Evaluations</h1>
        <p className="subtitle">Evaluation Results</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="category-selector">
        <label htmlFor="category-select">Select Evaluation Category:</label>
        <select
          id="category-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-dropdown"
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {availableTeams.length > 0 && (
        <div className="filter-section">
          <h3>Filter by Team</h3>
          <div className="team-filters">
            {availableTeams.map(team => (
              <label key={team} className="team-filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedTeams.includes(team)}
                  onChange={() => handleTeamToggle(team)}
                />
                <span>{team}</span>
              </label>
            ))}
          </div>
          {selectedTeams.length > 0 && (
            <button 
              className="clear-filters-btn"
              onClick={() => setSelectedTeams([])}
            >
              Clear Filters ({selectedTeams.length} selected)
            </button>
          )}
        </div>
      )}

      {trialPeriods.length > 0 && (
        <div className="filter-section">
          <h3>Filter by Trial Period</h3>
          <select
            value={selectedTrialPeriod}
            onChange={(e) => setSelectedTrialPeriod(e.target.value)}
            className="trial-period-select"
            style={{ width: '300px', padding: '8px', fontSize: '14px', borderRadius: '4px' }}
          >
            <option value="">All Time Periods</option>
            {trialPeriods.map(period => (
              <option key={period.id} value={period.id}>
                {period.name}
              </option>
            ))}
          </select>
          {selectedTrialPeriod && (
            <button 
              className="clear-filters-btn"
              onClick={() => setSelectedTrialPeriod('')}
              style={{ marginLeft: '10px' }}
            >
              Clear Period Filter
            </button>
          )}
        </div>
      )}

      {selectedCategoryData && (
        <div className="category-info">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>{selectedCategoryData.name}</h2>
              <p className="category-description">{selectedCategoryData.description}</p>
              <div className="metrics-list">
                <strong>Metrics:</strong> {actualMetrics.length > 0 ? actualMetrics.join(', ') : selectedCategoryData.metrics.join(', ')}
              </div>
            </div>
            {isAdmin && (
              <button className="add-evaluation-btn" onClick={() => handleAddEvaluation()}>
                + Add Player Evaluation
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading evaluation results...</div>
      ) : (
        <div className="results-container">
          {results.length === 0 ? (
            <div className="no-results">No evaluation results found for this category.</div>
          ) : (
            <div className="results-table-wrapper">
              <table className="results-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Player Name{getSortIndicator('name')}
                    </th>
                    <th onClick={() => handleSort('team')} className="sortable">
                      Team{getSortIndicator('team')}
                    </th>
                    {actualMetrics.map((metric) => (
                      <React.Fragment key={metric}>
                        <th className="rank-header">Rank</th>
                        <th onClick={() => handleSort(metric)} className="sortable">
                          {actualMetrics.length === 1 ? 'Score' : metric}{getSortIndicator(metric)}
                        </th>
                      </React.Fragment>
                    ))}
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedResults.map((result) => (
                    <React.Fragment key={result.id}>
                      <tr>
                        <td className="player-name">
                          {result.allEvaluations && result.allEvaluations.length > 1 && (
                            <button 
                              className="expand-btn"
                              onClick={() => toggleRowExpansion(result.player.id)}
                              title={`${result.allEvaluations.length} total tests`}
                            >
                              {expandedRows.has(result.player.id) ? '▼' : '▶'} 
                            </button>
                          )}
                          {result.player.firstName} {result.player.lastName}
                          {result.allEvaluations && result.allEvaluations.length > 1 && (
                            <span className="test-count-badge">{result.allEvaluations.length} tests</span>
                          )}
                        </td>
                      <td>{result.player.team?.name || 'N/A'}</td>
                      {actualMetrics.map((metric) => (
                        <React.Fragment key={metric}>
                          <td className="rank-cell">
                            {calculateRanks[metric]?.[result.id] || '-'}
                          </td>
                          <td 
                            className={`metric-value ${isAdmin ? 'editable-cell' : ''}`}
                            onClick={() => handleCellClick(result.id, metric, result.data[metric])}
                          >
                            {editingCell?.resultId === result.id && editingCell?.metric === metric ? (
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleCellBlur}
                                onKeyDown={handleCellKeyDown}
                                autoFocus
                                className="cell-edit-input"
                              />
                            ) : (
                              result.data[metric] !== undefined && result.data[metric] !== null
                                ? typeof result.data[metric] === 'number'
                                  ? (() => {
                                      // Determine decimal places based on category/metric type
                                      const categoryName = selectedCategoryData?.name.toLowerCase() || '';
                                      const metricName = metric.toLowerCase();
                                      
                                      // Timed drills: 3 decimal places (seconds with milliseconds)
                                      const isTimedDrill = 
                                        categoryName.includes('sprint') ||
                                        categoryName.includes('agility') ||
                                        categoryName.includes('dribble') ||
                                        categoryName.includes('figure') ||
                                        metricName.includes('time') ||
                                        metricName.includes('sprint') ||
                                        metricName.includes('dribble') ||
                                        metricName.includes('agility') ||
                                        metricName.includes('figure');
                                      
                                      // Beep Test: 1 decimal place (levels like 2.8, 4.1, etc.)
                                      const isBeepTest = categoryName.includes('beep');
                                      
                                      // Broad Jump: 2 decimal places (distances with precision)
                                      const isBroadJump = categoryName.includes('broad jump') || categoryName.includes('broad');
                                      
                                      // Default: 0 decimals (whole numbers for counts/scores)
                                      const decimals = isTimedDrill ? 3 : isBeepTest ? 1 : isBroadJump ? 2 : 0;
                                      return result.data[metric].toFixed(decimals);
                                    })()
                                  : result.data[metric]
                                : '-'
                            )}
                          </td>
                        </React.Fragment>
                      ))}
                       {isAdmin && (
                        <td className="actions-cell">
                          <button
                            className="add-retest-btn"
                            onClick={() => handleAddEvaluation(result.player.id)}
                            title="Add new evaluation (retest)"
                          >
                            ➕
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteEvaluation(
                              result.id,
                              `${result.player.firstName} ${result.player.lastName}`
                            )}
                            title="Delete evaluation"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                    
                    {/* Expanded history rows */}
                    {expandedRows.has(result.player.id) && result.allEvaluations && result.allEvaluations.length > 1 && (
                      <tr className="history-row">
                        <td colSpan={2 + (actualMetrics.length * 2) + (isAdmin ? 1 : 0)}>
                          <div className="history-container">
                            <h4>Test History for {result.player.firstName} {result.player.lastName}</h4>
                            <table className="history-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Trial Period</th>
                                  {actualMetrics.map(metric => (
                                    <th key={metric}>{metric}</th>
                                  ))}
                                  {isAdmin && <th>Actions</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {result.allEvaluations.map((evaluation, index) => (
                                  <tr key={evaluation.id}>
                                    <td>{new Date(evaluation.evaluationDate).toLocaleDateString()}</td>
                                    <td>{evaluation.trialPeriod?.name || 'N/A'}</td>
                                    {actualMetrics.map(metric => (
                                      <td 
                                        key={metric}
                                        className={isAdmin ? 'editable-cell' : ''}
                                        onClick={() => handleHistoryCellClick(evaluation.id, metric, evaluation.data[metric])}
                                        style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                                      >
                                        {editingHistoryCell?.evalId === evaluation.id && editingHistoryCell?.metric === metric ? (
                                          <input
                                            type="text"
                                            value={historyEditValue}
                                            onChange={(e) => setHistoryEditValue(e.target.value)}
                                            onBlur={handleHistoryCellBlur}
                                            onKeyDown={handleHistoryCellKeyDown}
                                            autoFocus
                                            className="cell-edit-input"
                                          />
                                        ) : (
                                          formatValue(evaluation.data[metric], metric)
                                        )}
                                      </td>
                                    ))}
                                    {isAdmin && (
                                      <td>
                                        <button
                                          className="delete-btn-small"
                                          onClick={() => handleDeleteHistoricalEvaluation(
                                            evaluation.id,
                                            `${result.player.firstName} ${result.player.lastName}`,
                                            evaluation.evaluationDate
                                          )}
                                          title="Delete this test"
                                        >
                                          🗑️
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

        <div className="evaluation-stats">
        <div className="stat-card">
          <h3>{categories.length}</h3>
          <p>Total Categories</p>
        </div>
        <div className="stat-card">
          <h3>{filteredAndSortedResults.length}</h3>
          <p>Players Shown</p>
        </div>
        <div className="stat-card">
          <h3>{actualMetrics.length || 0}</h3>
          <p>Metrics Displayed</p>
        </div>
      </div>

      {/* Add Player Evaluation Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Player Evaluation</h2>
            <p className="modal-subtitle">Category: {selectedCategoryData?.name}</p>
            
            <div className="form-group">
              <label>Select Player:</label>
              <select 
                value={selectedPlayer} 
                onChange={(e) => setSelectedPlayer(e.target.value)}
                className="player-select"
              >
                <option value="">-- Choose a player --</option>
                {allPlayers.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.firstName} {player.lastName} {player.team ? `(${player.team.name})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="metrics-input-grid">
              {selectedCategoryData?.metrics.map(metric => (
                <div key={metric} className="form-group">
                  <label>{metric}:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEvaluationData[metric] || ''}
                    onChange={(e) => setNewEvaluationData({
                      ...newEvaluationData,
                      [metric]: e.target.value
                    })}
                    placeholder="Enter value"
                  />
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowAddModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleSaveNewEvaluation} className="save-btn">
                Save Evaluation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluations;
